import React, { useCallback, useEffect, useState } from "react";
import { firestore } from "../../utils/firebase";
import { useHistory } from "react-router-dom";
import "./AddressList.css";

const emptyAddress = { phone: "", title: "", address: "" };

const normalizeAddress = (address = {}) => ({
  title: address.title || "Adres",
  address: address.address ?? address.addres ?? "",
});

const AddressList = () => {
  const [addresses, setAddresses] = useState([]);
  const [searchPhone, setSearchPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingKey, setEditingKey] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", address: "" });
  const [newAddress, setNewAddress] = useState(emptyAddress);
  const cafe = localStorage.getItem("name");
  const history = useHistory();

  const usersCollection = useCallback(
    () => firestore.collection(`Restaurants/${cafe}/users`),
    [cafe]
  );

  const mapUserAddresses = (doc) =>
    (doc.data().addressList || []).map((item, addressIndex) => ({
      ...normalizeAddress(item),
      id: doc.id,
      addressIndex,
      originalTitle: item.title || "Adres",
      originalAddress: item.address ?? item.addres ?? "",
    }));

  const fetchAllAddresses = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const snapshot = await usersCollection().get();
      setAddresses(
        snapshot.docs
          .flatMap(mapUserAddresses)
          .sort((first, second) => first.title.localeCompare(second.title, "tr"))
          .map((address) => ({ ...address, key: `${address.id}-${address.addressIndex}` }))
      );
    } catch (err) {
      setError(`Adresleri alırken hata oluştu: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [usersCollection]);

  const handleSearch = async () => {
    const phone = searchPhone.trim();
    if (!phone) {
      fetchAllAddresses();
      return;
    }

    setLoading(true);
    setError("");
    try {
      const doc = await usersCollection().doc(phone).get();
      if (!doc.exists || !doc.data().addressList?.length) {
        setAddresses([]);
        setError("Bu telefon numarasına ait kayıtlı adres bulunamadı.");
      } else {
        setAddresses(mapUserAddresses(doc).map((address) => ({ ...address, key: `${address.id}-${address.addressIndex}` })));
      }
    } catch (err) {
      setError(`Adresleri ararken hata oluştu: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setShowAddModal(false);
    setNewAddress(emptyAddress);
    setError("");
  };

  const handleAddAddress = async (event) => {
    event.preventDefault();
    const phone = newAddress.phone.trim();
    const title = newAddress.title.trim();
    const address = newAddress.address.trim();

    if (!phone || !title || !address) {
      setError("Telefon, adres başlığı ve adres alanları zorunludur.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const userRef = usersCollection().doc(phone);
      const doc = await userRef.get();
      const addressList = doc.exists ? doc.data().addressList || [] : [];
      await userRef.set(
        {
          addressList: [...addressList, { title, address }],
          updatedAt: new Date(),
          ...(doc.exists ? {} : { createdAt: new Date() }),
        },
        { merge: true }
      );
      resetModal();
      await fetchAllAddresses();
    } catch (err) {
      setError(`Adres eklenirken hata oluştu: ${err.message}`);
      setLoading(false);
    }
  };

  const updateAddressList = async (addressItem, updater) => {
    const userRef = usersCollection().doc(addressItem.id);
    await firestore.runTransaction(async (transaction) => {
      const doc = await transaction.get(userRef);
      if (!doc.exists) throw new Error("Müşteri kaydı bulunamadı.");

      const addressList = [...(doc.data().addressList || [])];
      const matchesOriginal = (item) =>
        (item.title || "Adres") === addressItem.originalTitle &&
        (item.address ?? item.addres ?? "") === addressItem.originalAddress;
      const index =
        addressList[addressItem.addressIndex] &&
        matchesOriginal(addressList[addressItem.addressIndex])
          ? addressItem.addressIndex
          : addressList.findIndex(matchesOriginal);

      if (index < 0) throw new Error("Adres kaydı bulunamadı veya başka bir işlemle değiştirildi.");
      transaction.update(userRef, {
        addressList: updater(addressList, index),
        updatedAt: new Date(),
      });
    });
  };

  const handleDelete = async (addressItem) => {
    if (!window.confirm(`“${addressItem.title}” adresini silmek istediğinize emin misiniz?`)) return;

    setLoading(true);
    setError("");
    try {
      await updateAddressList(addressItem, (addressList, index) =>
        addressList.filter((_, itemIndex) => itemIndex !== index)
      );
      if (editingKey === addressItem.key) setEditingKey(null);
      await fetchAllAddresses();
    } catch (err) {
      setError(`Adres silinirken hata oluştu: ${err.message}`);
      setLoading(false);
    }
  };

  const handleEdit = (addressItem) => {
    setEditingKey(addressItem.key);
    setEditForm({ title: addressItem.title, address: addressItem.address });
    setError("");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditForm({ title: "", address: "" });
  };

  const handleSave = async (addressItem) => {
    const title = editForm.title.trim();
    const address = editForm.address.trim();
    if (!title || !address) {
      setError("Adres başlığı ve adres alanları zorunludur.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await updateAddressList(addressItem, (addressList, index) =>
        addressList.map((item, itemIndex) =>
          itemIndex === index ? { ...item, title, address } : item
        )
      );
      cancelEdit();
      await fetchAllAddresses();
    } catch (err) {
      setError(`Adres güncellenirken hata oluştu: ${err.message}`);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAddresses();
  }, [fetchAllAddresses]);

  const goBack = () => history.push("/admin");

  return (
    <main className="address-page">
      <div className="address-shell">
        <header className="address-header">
          <button type="button" className="address-back" onClick={goBack} aria-label="Panele dön"><i className="bi bi-arrow-left" /></button>
          <div>
            <span className="address-eyebrow">YÖNETİM PANELİ</span>
            <h1>Adresler</h1>
            <p>Müşterilerinizin kayıtlı teslimat adreslerini tek yerden yönetin.</p>
          </div>
          <button type="button" className="address-primary-button" onClick={() => setShowAddModal(true)}><i className="bi bi-plus-lg" /> Yeni adres</button>
        </header>

        <section className="address-toolbar" aria-label="Adres arama">
          <div className="address-search-wrap">
            <i className="bi bi-search" />
            <input type="search" value={searchPhone} onChange={(event) => setSearchPhone(event.target.value)} onKeyDown={(event) => event.key === "Enter" && handleSearch()} placeholder="Telefon numarasıyla adres ara" aria-label="Telefon numarasıyla adres ara" />
          </div>
          <button type="button" className="address-search-button" onClick={handleSearch}>Ara</button>
          <button type="button" className="address-reset-button" onClick={() => { setSearchPhone(""); fetchAllAddresses(); }}><i className="bi bi-arrow-clockwise" /> Tümünü göster</button>
          <div className="address-count"><strong>{addresses.length}</strong><span>adres</span></div>
        </section>

        {error && <div className="address-alert" role="alert"><i className="bi bi-exclamation-circle" />{error}</div>}
        {loading && <div className="address-loading"><span className="spinner-border spinner-border-sm" /> Adresler yükleniyor...</div>}

        {!loading && addresses.length === 0 ? (
          <section className="address-empty">
            <div className="address-empty-icon"><i className="bi bi-geo-alt" /></div>
            <h2>Henüz adres yok</h2>
            <p>Telefon numarasıyla arama yapabilir veya yeni bir adres ekleyebilirsiniz.</p>
            <button type="button" className="address-primary-button" onClick={() => setShowAddModal(true)}><i className="bi bi-plus-lg" /> İlk adresi ekle</button>
          </section>
        ) : (
          <section className="address-grid" aria-label="Kayıtlı adresler">
            {addresses.map((address) => (
              <article className="address-card" key={address.key}>
                {editingKey === address.key ? (
                  <div className="address-edit-form">
                    <div className="address-card-heading"><div className="address-card-icon"><i className="bi bi-pencil" /></div><div><span className="address-card-label">ADRESİ DÜZENLE</span><strong>{address.id}</strong></div></div>
                    <label htmlFor={`title-${address.key}`}>Adres başlığı</label>
                    <input id={`title-${address.key}`} type="text" value={editForm.title} onChange={(event) => setEditForm({ ...editForm, title: event.target.value })} />
                    <label htmlFor={`address-${address.key}`}>Adres</label>
                    <textarea id={`address-${address.key}`} rows="4" value={editForm.address} onChange={(event) => setEditForm({ ...editForm, address: event.target.value })} />
                    <div className="address-actions"><button type="button" className="address-save-button" onClick={() => handleSave(address)} disabled={loading}><i className="bi bi-check-lg" /> Kaydet</button><button type="button" className="address-cancel-button" onClick={cancelEdit} disabled={loading}>İptal</button></div>
                  </div>
                ) : (
                  <>
                    <div className="address-card-topline">
                      <div className="address-card-heading"><div className="address-card-icon"><i className="bi bi-geo-alt" /></div><div><span className="address-card-label">{address.title}</span><strong><i className="bi bi-telephone" /> {address.id}</strong></div></div>
                      <div className="address-card-menu"><button type="button" onClick={() => handleEdit(address)} aria-label="Adresi düzenle" title="Adresi düzenle"><i className="bi bi-pencil" /><span>Düzenle</span></button><button type="button" className="is-danger" onClick={() => handleDelete(address)} aria-label="Adresi sil" title="Adresi sil"><i className="bi bi-trash3" /><span>Sil</span></button></div>
                    </div>
                    <p className="address-card-text">{address.address}</p>
                  </>
                )}
              </article>
            ))}
          </section>
        )}
      </div>

      {showAddModal && (
        <div className="address-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && resetModal()}>
          <div className="address-modal" role="dialog" aria-modal="true" aria-labelledby="new-address-title">
            <div className="address-modal-header"><div><span className="address-eyebrow">YENİ KAYIT</span><h2 id="new-address-title">Adres ekle</h2></div><button type="button" onClick={resetModal} aria-label="Pencereyi kapat"><i className="bi bi-x-lg" /></button></div>
            <form onSubmit={handleAddAddress}>
              <div className="address-modal-body">
                <label htmlFor="new-phone">Telefon numarası</label><input id="new-phone" type="tel" value={newAddress.phone} onChange={(event) => setNewAddress({ ...newAddress, phone: event.target.value })} placeholder="05xx xxx xx xx" autoFocus />
                <label htmlFor="new-title">Adres başlığı</label><input id="new-title" type="text" value={newAddress.title} onChange={(event) => setNewAddress({ ...newAddress, title: event.target.value })} placeholder="Ev, İş, vb." />
                <label htmlFor="new-address">Adres</label><textarea id="new-address" rows="4" value={newAddress.address} onChange={(event) => setNewAddress({ ...newAddress, address: event.target.value })} placeholder="Tam adresi girin" />
                {error && <div className="address-alert address-modal-alert" role="alert"><i className="bi bi-exclamation-circle" />{error}</div>}
              </div>
              <div className="address-modal-footer"><button type="button" className="address-cancel-button" onClick={resetModal}>İptal</button><button type="submit" className="address-primary-button" disabled={loading}><i className="bi bi-check-lg" /> Adresi kaydet</button></div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default AddressList;
